import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  ButtonGroup,
  Switch,
  FormControlLabel,
  TextField,
  IconButton,
} from '@mui/material';
import { ChevronLeft, ChevronRight } from '@mui/icons-material';
import FolkSaptapurHeader from './FolkSaptapurHeader';
import FolkSaptapurTabs from './FolkSaptapurTabs';
import { folkSaptapurAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';

const DOW = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const JAPA_MAX = 32;

function isJapaInvalid(value) {
  return value !== null && value !== undefined && (value < 0 || value > JAPA_MAX);
}

// FastAPI validation errors (422) return `detail` as an array of
// {type, loc, msg, ...} objects rather than a string - render that raw
// would crash React, so flatten it to plain text.
function formatApiError(err, fallback) {
  const detail = err.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map((d) => d.msg || JSON.stringify(d)).join('; ');
  return fallback;
}

function startOfWeek(d) {
  const date = new Date(d);
  date.setHours(0, 0, 0, 0);
  date.setDate(date.getDate() - date.getDay());
  return date;
}

function toDateStr(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function MorningSadhana() {
  const { isAdmin } = useAuth();
  const admin = isAdmin();
  const today = useMemo(() => { const t = new Date(); t.setHours(0, 0, 0, 0); return t; }, []);
  const yesterday = useMemo(() => { const d = new Date(today); d.setDate(d.getDate() - 1); return d; }, [today]);
  const [weekOffset, setWeekOffset] = useState(0);
  const [selectedDate, setSelectedDate] = useState(today);
  const [rows, setRows] = useState([]);
  const [classNotConducted, setClassNotConducted] = useState(false);
  const [specialActive, setSpecialActive] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);

  // Per-date response cache so re-visiting a day (e.g. toggling between
  // today/yesterday, or Cancel reverting unsaved edits) is instant instead
  // of re-hitting the DB every time. `reqSeqRef` lets a slow, now-stale
  // request be ignored if the user has since switched to another day.
  const cacheRef = useRef({});
  const reqSeqRef = useRef(0);

  const weekStart = useMemo(() => {
    const base = startOfWeek(today);
    base.setDate(base.getDate() + weekOffset * 7);
    return base;
  }, [today, weekOffset]);

  const week = useMemo(() => Array.from({ length: 7 }, (_, i) => {
    const d = new Date(weekStart);
    d.setDate(d.getDate() + i);
    return d;
  }), [weekStart]);

  const applyData = (data) => {
    setClassNotConducted(data.class_not_conducted);
    setSpecialActive(data.special_class_active);
    setRows(data.rows);
  };

  // `silent` prefetches only warm the cache for a date that isn't the one
  // on screen right now - they must never touch visible state, and must
  // never count against the sequence number that guards the real load.
  const load = async (date, { silent } = {}) => {
    const key = toDateStr(date);
    const cached = cacheRef.current[key];

    if (!silent) {
      setError('');
      if (cached) {
        applyData(cached);
        setLoading(false);
        return; // already have it - no need to hit the network at all
      }
      setLoading(true);
    } else if (cached) {
      return;
    }

    const seq = silent ? null : ++reqSeqRef.current;
    try {
      const response = await folkSaptapurAPI.getDayAttendance(key);
      const data = response.data;
      cacheRef.current[key] = data;
      if (!silent && seq === reqSeqRef.current) applyData(data);
    } catch (err) {
      if (!silent && seq === reqSeqRef.current) setError(formatApiError(err, 'Failed to load attendance'));
    } finally {
      if (!silent && seq === reqSeqRef.current) setLoading(false);
    }
  };

  useEffect(() => {
    load(selectedDate);
    // Warm the cache for the other half of the today/yesterday toggle in the
    // background, so switching between them feels instant either direction.
    const other = toDateStr(selectedDate) === toDateStr(today) ? yesterday : today;
    load(other, { silent: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDate]);

  // Non-admins are limited to today/yesterday - the backend enforces this
  // too (the real boundary), this is just for the UI to match.
  const isDayAllowed = (d) => {
    if (d > today) return false;
    if (admin) return true;
    return toDateStr(d) === toDateStr(today) || toDateStr(d) === toDateStr(yesterday);
  };

  const selectDay = (d) => {
    if (!isDayAllowed(d)) return;
    setSelectedDate(d);
    setMessage('');
  };

  const setStatus = (studentId, status) => {
    setRows((prev) => prev.map((r) => (r.student_id === studentId ? { ...r, status } : r)));
  };

  const setJapa = (studentId, value) => {
    setRows((prev) => prev.map((r) => (r.student_id === studentId ? { ...r, japa_rounds: value === '' ? null : parseInt(value, 10) } : r)));
  };

  const setSpecialAnswer = (studentId, answer) => {
    setRows((prev) => prev.map((r) => (r.student_id === studentId ? { ...r, special_class_answer: answer } : r)));
  };

  const markAll = (status) => {
    setRows((prev) => prev.map((r) => ({ ...r, status })));
  };

  const handleSave = async () => {
    setError('');

    if (rows.some((r) => isJapaInvalid(r.japa_rounds))) {
      setError(`Japa Rounds must be between 0 and ${JAPA_MAX} - fix the highlighted field(s) before saving.`);
      return;
    }

    try {
      await folkSaptapurAPI.saveDayAttendance({
        attendance_date: toDateStr(selectedDate),
        class_not_conducted: classNotConducted,
        rows: rows.map((r) => ({ student_id: r.student_id, status: r.status, japa_rounds: r.japa_rounds })),
        special_class_answers: specialActive
          ? rows
              .filter((r) => r.special_class_answer !== null && r.special_class_answer !== undefined)
              .map((r) => ({ student_id: r.student_id, answer_date: toDateStr(selectedDate), answer: r.special_class_answer }))
          : [],
      });
      setMessage('Attendance saved');
      delete cacheRef.current[toDateStr(selectedDate)]; // stale now - force a fresh fetch
      load(selectedDate);
      setTimeout(() => setMessage(''), 3000);
    } catch (err) {
      setError(formatApiError(err, 'Failed to save attendance'));
    }
  };

  const weekLabel = `Week of ${week[0].getDate()} ${week[0].toLocaleString('default', { month: 'short' })} – ${week[6].getDate()} ${week[6].toLocaleString('default', { month: 'short' })} ${week[6].getFullYear()}`;

  return (
    <Box>
      <FolkSaptapurHeader />
      <FolkSaptapurTabs />
      <Container maxWidth="lg" sx={{ mt: 3, mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Typography variant="h6">Morning Sadhana</Typography>
          <ButtonGroup size="small">
            <Button onClick={() => markAll('P')}>Mark all Present</Button>
            <Button onClick={() => markAll('A')}>Mark all Absent</Button>
          </ButtonGroup>
        </Box>
        <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
          Pick a day this week, then mark each student Present, Absent, or Went to Native
        </Typography>

        {message && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setMessage('')}>{message}</Alert>}
        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Typography variant="body2" color="textSecondary" fontWeight={600}>{weekLabel}</Typography>
          <Box>
            <IconButton size="small" disabled={!admin} onClick={() => setWeekOffset((w) => w - 1)}><ChevronLeft fontSize="small" /></IconButton>
            <IconButton size="small" disabled={weekOffset >= 0} onClick={() => setWeekOffset((w) => w + 1)}><ChevronRight fontSize="small" /></IconButton>
          </Box>
        </Box>

        {!admin && (
          <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mb: 1 }}>
            You can only view or edit today's and yesterday's Morning Sadhana. Contact an Admin for older dates.
          </Typography>
        )}

        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 1, mb: 2 }}>
          {week.map((d) => {
            const isSelected = toDateStr(d) === toDateStr(selectedDate);
            return (
              <Button
                key={toDateStr(d)}
                variant={isSelected ? 'contained' : 'outlined'}
                disabled={!isDayAllowed(d)}
                onClick={() => selectDay(d)}
                sx={{ flexDirection: 'column', py: 1, lineHeight: 1.3 }}
              >
                <Typography variant="caption" sx={{ display: 'block', fontWeight: 700 }}>{DOW[d.getDay()]}</Typography>
                <Typography variant="body1" sx={{ fontWeight: 700 }}>{d.getDate()}</Typography>
              </Button>
            );
          })}
        </Box>

        <Paper variant="outlined" sx={{ p: 1.5, display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <FormControlLabel
            control={<Switch checked={classNotConducted} onChange={(e) => setClassNotConducted(e.target.checked)} />}
            label=""
          />
          <Box>
            <Typography variant="body2" fontWeight={600}>Class Not Conducted on this day</Typography>
            <Typography variant="caption" color="textSecondary">
              Turn this on instead of marking students individually — the day is left out of everyone's total, not counted as absent.
            </Typography>
          </Box>
        </Paper>

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mb: 2 }}>
          <Button onClick={() => load(selectedDate)}>Cancel</Button>
          <Button variant="contained" onClick={handleSave}>Save Attendance</Button>
        </Box>

        {classNotConducted && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            This day is marked "Class Not Conducted" — no Present/Absent/Native marking needed. It will be skipped entirely in the monthly total.
          </Alert>
        )}

        <TableContainer
          component={Paper}
          variant="outlined"
          sx={{
            opacity: loading ? 0.5 : classNotConducted ? 0.4 : 1,
            pointerEvents: loading || classNotConducted ? 'none' : 'auto',
            transition: 'opacity 0.1s',
          }}
        >
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell><strong>Sl No</strong></TableCell>
                <TableCell><strong>Student Name</strong></TableCell>
                <TableCell align="center"><strong>Morning Sadhana</strong></TableCell>
                <TableCell align="center"><strong>Japa Rounds</strong></TableCell>
                <TableCell align="center"><strong>Weekly Special Class</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((r, idx) => (
                <TableRow key={r.student_id} hover>
                  <TableCell>{idx + 1}</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>{r.name}</TableCell>
                  <TableCell align="center">
                    <ButtonGroup size="small">
                      <Button variant={r.status === 'P' ? 'contained' : 'outlined'} color="success" onClick={() => setStatus(r.student_id, 'P')}>P</Button>
                      <Button variant={r.status === 'A' ? 'contained' : 'outlined'} color="error" onClick={() => setStatus(r.student_id, 'A')}>A</Button>
                      <Button variant={r.status === 'N' ? 'contained' : 'outlined'} color="info" onClick={() => setStatus(r.student_id, 'N')}>N</Button>
                    </ButtonGroup>
                  </TableCell>
                  <TableCell align="center">
                    <TextField
                      size="small"
                      type="number"
                      value={r.japa_rounds ?? ''}
                      onChange={(e) => setJapa(r.student_id, e.target.value)}
                      error={isJapaInvalid(r.japa_rounds)}
                      sx={{ width: 70 }}
                      inputProps={{ min: 0, max: JAPA_MAX, style: { textAlign: 'center' } }}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <ButtonGroup size="small" disabled={!specialActive}>
                      <Button variant={r.special_class_answer === true ? 'contained' : 'outlined'} color="success" onClick={() => setSpecialAnswer(r.student_id, true)}>Yes</Button>
                      <Button variant={r.special_class_answer === false ? 'contained' : 'outlined'} color="error" onClick={() => setSpecialAnswer(r.student_id, false)}>No</Button>
                    </ButtonGroup>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 2 }}>
          Japa Rounds is a plain number entered per student per day, from 0 up to a maximum of {JAPA_MAX}. Weekly
          Special Class is live Yes/No for every student only on the day(s) configured on Assign Service Tasks.
        </Typography>
      </Container>
    </Box>
  );
}

export default MorningSadhana;
